<template>
  <v-app :style="{ background: $vuetify.theme.themes.light.background }">
    <component :is="layout" class="app-layout">
      <v-overlay opacity="0.8" color="primary" style='z-index:6;' :value="showLoader">
        <v-progress-circular indeterminate color="orange" :size="70" :width="7" />
      </v-overlay>
      <router-view />
    </component>
    <v-snackbar
      v-model="errorState.visible"
      :color="errorState.color"
      :timeout="8000"
      top
      multi-line
    >
      {{ errorState.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="errorState.visible = false">Dismiss</v-btn>
      </template>
    </v-snackbar>
    <v-btn
      v-if="feedbackButtonVisible"
      class="feedback-button"
      aria-label="Send feedback"
      color="white"
      fixed
      right
      @click="openFeedback"
    >
      <img src="./assets/logo.svg" alt="" class="feedback-logo" />
      <span class="feedback-label">Feedback</span>
    </v-btn>
    <v-dialog v-model="feedbackDialog" max-width="560">
      <v-card>
        <v-card-title>Asca Portal Feedback</v-card-title>
        <v-card-text>
          <p>
            This form is for feedback about Asca Portal itself. For membership,
            registration, or other ASCA matters, contact the
            <a href="https://asca.org/asca/business-office/" target="_blank" rel="noopener noreferrer">ASCA Business Office</a>.
          </p>
          <v-textarea
            v-model="feedbackMessage"
            label="Portal feedback"
            placeholder="Tell us about a problem or suggestion for Asca Portal."
            outlined
            auto-grow
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="feedbackDialog = false">Cancel</v-btn>
          <v-btn color="primary" :disabled="!feedbackMessage.trim()" @click="submitFeedback">Send Feedback</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="feedbackSubmitted" color="success" :timeout="5000">
      Thank you for your feedback.
    </v-snackbar>
    <v-footer app color="#FAFAFA" class="site-footer">
      <span>© The Australian Shepherd Club of America</span>
      <span v-if="releaseVersion && publishedAt">
        · Release {{ releaseVersion }} · Updated <localized-date-time :instant="publishedAt" />
      </span>
    </v-footer>
    <CookePrompt />
  </v-app>
</template>

<script lang="ts">
import Vue from 'vue';
import userService from "@/services/portal/userService";
import authService from "./services/authenticationService";
import decodeToken from "@/helpers/decode-token";
import getDefaultRoute from "@/helpers/default-route";
import axios from 'axios';
import * as Sentry from '@sentry/vue';
import CookePrompt from '@/components/common/CookiePrompt.vue'
import { env } from './environment/environment';
import { errorState } from '@/services/errorHandler';
const default_layout = "default";

export default Vue.extend({
  name: 'App',
  data: () => ({
    tokenExpirationTimer: null as number | null,
    errorState,
    feedbackDialog: false,
    feedbackMessage: '',
    feedbackSubmitted: false,
  }),
  components: {
    CookePrompt,
  },
  methods: {
    openFeedback: function () {
      this.feedbackDialog = true;
      Sentry.getReplay()?.flush();
    },
    submitFeedback: function () {
      const message = this.feedbackMessage.trim();
      if (!message) return;

      const userInfo = this.$store.state.userInfo;
      const name = [userInfo.firstName, userInfo.lastName].filter(Boolean).join(' ');
      Sentry.captureFeedback({
        name,
        email: userInfo.email,
        message,
      }, {
        includeReplay: true,
        captureContext: {
          user: {
            id: String(userInfo.personId),
            username: userInfo.username,
            name,
            email: userInfo.email,
          },
        },
      });

      this.feedbackMessage = '';
      this.feedbackDialog = false;
      this.feedbackSubmitted = true;
    },
    userLoaded: function (args: any) {

      this.setTimer(args.detail.token)
      userService.getProfile().then(userInfo => {
        if (localStorage.getItem("userToken") !== args.detail.token) return;

        this.$store.commit('loadUserInfo', userInfo);
        Sentry.setUser({
          id: String(userInfo.personId),
          username: userInfo.username,
          name: [userInfo.firstName, userInfo.lastName].filter(Boolean).join(' '),
          email: userInfo.email,
        });
        Sentry.setTag('user.role', userInfo.isAdmin ? 'admin' : 'member');
        Sentry.setTag('membership.active', String(userInfo.isActiveMember));
        if (userInfo.isAdmin) {
          this.$store.dispatch("loadAdminTasks", {}).catch(Sentry.captureException);
          if (this.$route?.path === '/') {
            this.$router.replace(getDefaultRoute(userInfo)).catch(this.$handleNavigationError);
          }
        } else if (!userInfo.hasAgreedToPolicies) {
          this.$store.commit('changeAgreementFor', "ExistingAccount")
          if (this.$route.path !== '/agreements') this.$navigate('/agreements');
        } else if (userInfo.hasNeverBeenMember) {
          if (this.$route?.path === '/') {
            this.$router.replace(getDefaultRoute(userInfo)).catch(this.$handleNavigationError);
          }
        }else if (userInfo.juniorHasToRegister) {
          if (this.$route.path !== '/expired-membership') this.$navigate('/expired-membership');
        } else if (!userInfo.lastPromptedForMembershipAt) {
          if (this.$route.path !== '/gain-membership') this.$navigate('/gain-membership');
        } else if (!userInfo.isActiveMember) {
          if (this.$route.path !== '/expired-membership') this.$navigate('/expired-membership');
        } else if (this.$route?.path === '/') {
          this.$router.replace(getDefaultRoute(userInfo)).catch(this.$handleNavigationError);
        }
        this.$store.dispatch("loadPersonTasks").catch(Sentry.captureException);
      }).catch(() => this.logoutUser());
    },
    refreshToken: function () {
      authService.refresh()
        .then(d => {
          if (!d.token) return this.logoutUser()
          localStorage.setItem("userToken", d.token)
          if (this.$store.state.userInfo) {
            this.setTimer(d.token)
          } else {
            this.userLoaded({ detail: { token: d.token } });
          }
        }).catch(e=> {
          this.logoutUser();
        })
    },
    logoutUser: function () {
      axios.defaults.headers.common["Authorization"] = null;
      localStorage.removeItem("userToken");
      this.$store.commit("loadUserInfo", null);
      Sentry.setUser(null);
      Sentry.setTag('user.role', 'anonymous');
      Sentry.setTag('membership.active', 'false');
      if (this.$route.path !== '/login') this.$navigate('/login');
    }, 
    onExpiredToken: function () {
      const rememberMe = JSON.parse(localStorage.getItem("rememberMe") || "false");
      if (!rememberMe) return this.logoutUser();
      this.refreshToken();
    },
    setTimer(token: string) {
      if (this.tokenExpirationTimer) clearTimeout(this.tokenExpirationTimer)
      const decoded: any = decodeToken(token);
      const expiresIn = (decoded.exp * 1000) - Date.now();
      const rememberMe = JSON.parse(localStorage.getItem("rememberMe") || "false");
      const refreshLeadTime = rememberMe ? 60_000 : 0;
      this.tokenExpirationTimer = setTimeout(this.onExpiredToken, Math.max(expiresIn - refreshLeadTime, 0));
    },
  },
  computed: {
    feedbackEnabled(): boolean {
      return Boolean(env.VUE_APP_SENTRY_DSN)
        && Boolean(this.$store.state.userInfo)
        && this.$route.path !== '/login'
        && this.$route.path !== '/logout';
    },
    feedbackButtonVisible(): boolean {
      return this.feedbackEnabled && !this.feedbackDialog;
    },
    layout() {
      return (this.$route.meta?.layout || default_layout) + '-layout';
    },
    showLoader() {
      return this.$store.state.showLoader;
    },
    releaseVersion(): string {
      return env.VUE_APP_SENTRY_RELEASE || '';
    },
    publishedAt(): Temporal.Instant | null {
      try {
        return env.VUE_APP_PUBLISHED_AT_UTC
          ? Temporal.Instant.from(env.VUE_APP_PUBLISHED_AT_UTC)
          : null;
      } catch {
        return null;
      }
    }
  },
  mounted() {
    window.addEventListener('userLoaded', this.userLoaded);

    const token = localStorage.getItem("userToken");
    if (token && !this.$store.state.userInfo) {
      const decoded: any = decodeToken(token);
      if (Date.now() >= (Number(decoded.exp) * 1000)) {
        this.onExpiredToken();
        return;
      }
      const event = new CustomEvent("userLoaded", { detail: { personId: decoded.nameid, token: token } });
      window.dispatchEvent(event);
    }

  },
  destroyed() {
    window.removeEventListener('userLoaded', this.userLoaded)
    if (this.tokenExpirationTimer) clearTimeout(this.tokenExpirationTimer)
  }
});
</script>

<style scoped>
.app-layout {
  padding-bottom: 72px;
}

.site-footer {
  min-height: 18px !important;
  padding: 2px 8px !important;
  color: rgba(0, 0, 0, 0.45);
  font-size: 8pt;
  line-height: 1.2;
  justify-content: flex-end;
}

.feedback-button {
  bottom: 28px;
  text-transform: none;
  z-index: 7;
}

.feedback-logo {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}

@media (max-width: 600px) {
  .feedback-button {
    min-width: 0 !important;
    padding: 0 10px !important;
  }

  .feedback-logo {
    margin-right: 0;
  }

  .feedback-label {
    display: none;
  }
}
</style>
